<template>
  <div
    v-if="!props.isprintView"
    :id="props.project.id"
    ref="projectEl"
    class="sidebar-group"
    :class="{ open: props.isopen, subproject: !!props.project.parent_id }"
    :style="sidebarGroupStyle"
    @click="emit('update', props.project.id)"
  >
    <div class="flex items-center justify-between w-full px-2">
      <div class="flex items-center gap-2">
        <ChevronRight :size="16" v-if="!props.isopen"/>
        <ChevronDown  :size="16" v-if="props.isopen"/>
        <!-- Die Art eines Bereichs steht jetzt am Namen statt in einer eigenen
             Ueberschriftszeile (Entwurf Bjoern): ein Haken fuer Leistungs-
             bereiche, ein Symbol fuer Unterkuenfte. Eine neue Kategorie braucht
             einen Eintrag in KAT_ICON, sonst bekommt sie den Haken. -->
        <component v-if="props.project.parent_id" :is="katIcon" :size="13" class="cat-icon" />
        <span v-if="props.project.parent_id" class="sub-name"
              :title="CATEGORIES[props.project.category]"
              :style="{ borderColor: props.project.color }">{{ props.project.name }}</span>
        <template v-else>{{ props.project.name }}</template>
      </div>

      <div v-show="props.isopen" class="flex items-center">
        <!-- Projektmenue (Entwurf Bjoern): bisher ein Eintrag, spaeter der Ort
             fuer alles Projektweite. Nur am Hauptprojekt - ein Bereich bekommt
             selbst keine Bereiche (eine Ebene, TIM-73). -->
        <div v-if="!props.project.parent_id">
          <Button variant="ghost" size="icon" @click.stop="toggleMenu">
            <MoreHorizontal :size="16" />
          </Button>
          <!-- An den Body gehaengt: .sidebar-group hat overflow:hidden bei
               fester Zeilenhoehe, ein Menue darunter waere abgeschnitten - der
               Knopf sah dadurch aus, als taete er gar nichts. -->
          <Teleport to="body">
            <div v-if="showMenu" class="project-menu"
                 :style="{ top: `${menuPos.top}px`, left: `${menuPos.left}px` }"
                 @click.stop @pointerdown.stop>
              <button class="project-menu-item"
                      @click="showMenu = false; emit('addSubproject', props.project)">
                Bereich anlegen …
              </button>
            </div>
          </Teleport>
        </div>
        <Button variant="ghost" size="icon" @click.stop="showPersonModal = true">
          <Filter :size="hasOwnFilter ? 19 : 16"
                  :stroke-width="hasOwnFilter ? 2.6 : 2"
                  :style="hasOwnFilter ? { color: FILTER_AKTIV } : undefined" />
        </Button>
        <Button variant="ghost" size="icon" @click.stop="activePrintMenu = !activePrintMenu">
          <Printer :size="16"/>
        </Button>
      </div>
    </div>

    <PersonSelectionModal
      :open="showPersonModal"
      :project="props.project"
      :users="props.users"
      :bookings="props.bookings"
      :projects="props.projects ?? []"
      :resourceFilter="props.resourceFilter.filter"
      @apply="(filter) => { emit('updateResourceFilter', props.project.id, filter); showPersonModal = false }"
      @cancel="showPersonModal = false"
    />

    <PrintCallout
      :id="`print-${props.project.id}`"
      :target="projectEl"
      :show="activePrintMenu"
      :project="props.project"
	    @close="activePrintMenu = false"
      @print-view="handlePrintAction"
    />
  </div>

  <div v-if="props.isopen">
    <div v-for="(u, idx) in filteredUsers" :key="u.id" :style="userRowStyle(idx)" class="content" :class="{printview: props.isprintView}">
      <div class="inner">{{ u.firstname }} {{ props.isprintView ? u.lastname : u.short }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import { getRowHeight, applyResoureFilter, CATEGORIES } from "./planner.js"
import { readableProjectColor } from "@/utils/globals"
import { useViewStore } from "@/stores/viewStore"
import PersonSelectionModal from './PersonSelectionModal.vue'
import PrintCallout from './PrintCallout.vue'
import { Button } from "@/components/ui/button"
import { ChevronRight, ChevronDown, Printer, Filter, MoreHorizontal, CornerDownRight, BedDouble } from "lucide-vue-next"

// Symbol je Bereichsart. Bewusst hier und nicht bei CATEGORIES: die Schluessel
// kommen vom Server, die Symbole gehoeren zur Oberflaeche.
const KAT_ICON = { service: CornerDownRight, accommodation: BedDouble }

// abstandUnten: wahr, wenn hier ein Projektblock endet - dann laesst der
// Farbbalken an seiner letzten Zeile unten eine Luecke.
const props = defineProps(['project', 'users', 'bookings', 'isopen', 'resourceFilter', 'isprintView', 'projects', 'abstandUnten'])
const emit = defineEmits(['update', 'updateResourceFilter', 'printView', 'addSubproject'])

const projectEl = ref(null)
const activePrintMenu = ref(false)
const showPersonModal = ref(false)
const view = useViewStore()
const showMenu = ref(false)

const katIcon = computed(() => KAT_ICON[props.project.category] ?? CornerDownRight)

// Das Menue steht am Body und braucht deshalb feste Koordinaten - genommen vom
// Knopf, an dem es haengt.
const menuPos = ref({ top: 0, left: 0 })

function toggleMenu(e) {
  if (showMenu.value) { showMenu.value = false; return }
  const r = e.currentTarget.getBoundingClientRect()
  menuPos.value = { top: r.bottom + 4, left: r.left }
  showMenu.value = true
}

// Ein Klick daneben schliesst es. Erst im naechsten Durchlauf anmelden, sonst
// faengt derselbe Klick, der es geoeffnet hat, den Zuhoerer gleich wieder ein.
//
// Der Zuhoerer haengt an pointerdown, @click.stop haelt aber nur click auf -
// deshalb stoppt das Menue pointerdown zusaetzlich selbst. Ohne das schloss ein
// Klick auf "Bereich anlegen" erst das Menue, und der click landete dann im
// Nichts, weil der Knopf schon weg war.
function closeMenu() { showMenu.value = false }

watch(showMenu, (offen) => {
  if (offen) setTimeout(() => document.addEventListener('pointerdown', closeMenu, { once: true }), 0)
  else document.removeEventListener('pointerdown', closeMenu)
})

onUnmounted(() => document.removeEventListener('pointerdown', closeMenu))

const filteredUsers = computed(() =>
  props.users.filter((u: any) => applyResoureFilter(props.resourceFilter, props.project, u))
)

// Hinweis am Filtersymbol, wenn hier ein eigener Personenfilter aktiv ist —
// Björns Wunsch aus dem Mai-Thread in TIM-18.
const hasOwnFilter = computed(() => props.resourceFilter.filter?.mode === 'custom')

// Das Marken-Gruen (#1EFF00 in der Tailwind-Konfiguration) ist als duenner
// Strich auf weissem Grund kaum zu sehen: Neon hat auf hellem Untergrund fast
// keinen Kontrast. Dieselbe Abdunkelung wie bei den Projektfarben - der Farbton
// bleibt, die Erkennbarkeit kommt dazu.
//
// Bewusst nur hier und nicht an der Markenfarbe selbst: die traegt auch die
// gruenen Knoepfe, wo sie auf dunkler Schrift richtig ist.
const FILTER_AKTIV = readableProjectColor('#1EFF00', 4.5)

// Farbbalken am linken Rand (Entwurf Bjoern, Frame 75). Er sitzt an jeder Zeile
// einzeln statt als ein Element ueber alle: so entsteht er von selbst in der
// richtigen Laenge, egal was gerade auf- oder zugeklappt ist.
//
// Die Farbe kommt als CSS-Variable, die Zeichnung macht das Stylesheet - als
// Rahmen ginge es nicht, weil der Balken an den Uebergaengen eine Luecke lassen
// und bei einem Bereich abzweigen soll.
//
// Abgedunkelt, weil die rohe Pastellfarbe als 4-px-Leiste auf hellem Grund kaum
// zu sehen ist - dieselbe Farbe wie der Rahmen um das Projekt.
const balkenFarbe = computed(() => readableProjectColor(props.project.color, 3))

// Um so viel ruecken die Zeilen eines Bereichs ein - und genau so lang ist der
// Haken, mit dem der Balken zu ihnen abbiegt. Eine Zahl fuer beides, sonst
// treffen sich Strich und Text nicht.
const EINZUG = 14

// Der Balken setzt genau einmal aus: am Ende eines Projektblocks, auf der
// Stufe, auf der er gerade endet. Alles andere - Personenzeilen, Bereiche, der
// Haken - laeuft ineinander ueber.
const SPALTE = 2

// Eine Staerke fuer alle Striche - Kopfzeilen wie Personenzeilen. Zwei
// Abstufungen liessen den Balken je nach Zeile anders aussehen, ohne dass der
// Unterschied etwas bedeutet haette; die Ebene sagt schon die Einrueckung.
const STRICH = 3

// Wo genau die Luecke sitzt, haengt davon ab, was die letzte Zeile ist: bei
// einem zugeklappten Projekt die Kopfzeile, sonst die letzte Personenzeile.
const endetInKopfzeile = computed(() =>
  props.abstandUnten && !(props.isopen && filteredUsers.value.length > 0))

const sidebarGroupStyle = computed(() => ({
  height: `${getRowHeight(props.isprintView)}px`,
  '--balken': balkenFarbe.value,
  '--einzug': `${EINZUG}px`,
  '--spalte': `${SPALTE}px`,
  '--strich': `${STRICH}px`,
  '--balken-unten': endetInKopfzeile.value ? `${SPALTE}px` : '0px',
}))

function userRowStyle(idx: number | string) {
  // Personen eines Bereichs stehen eine Stufe weiter innen als die des
  // Hauptprojekts - sonst haengen sie optisch am falschen Knoten (Wunsch
  // Bjoern). Dieselben 14 px, um die auch der Bereichsname eingerueckt ist.
  return {
    height: `${getRowHeight(props.isprintView)}px`,
    // 8 px Grundpolsterung plus EINZUG - der Text steht damit genau dort, wo
    // der Balken nach dem Haken weiterlaeuft.
    paddingLeft: props.project.parent_id ? `${8 + EINZUG}px` : undefined,
    '--balken': balkenFarbe.value,
    '--balken-links': props.project.parent_id ? `${EINZUG}px` : '0px',
    '--strich': `${STRICH}px`,
    '--balken-unten': (props.abstandUnten && Number(idx) === filteredUsers.value.length - 1)
      ? `${SPALTE}px` : '0px',
  }
}

function handlePrintAction(start: string, end: string) {
    emit('printView', props.project, start, end);
}
</script>

<style scoped>

.sidebar-group {
  border-top: 1px solid #ccc;
  overflow: hidden;
  transition: height 0.3s ease;
  display: flex;
  position: relative;
}

.content { position: relative; }

/* Farbbalken links (Entwurf Bjoern). Als Pseudo-Element statt als Rahmen, weil
   er an den Uebergaengen aussetzen soll: eine schmale Luecke ueber jeder
   Kopfzeile - vor einem neuen Projekt wie vor einem Bereich. Ohne die liest
   sich die ganze Seitenleiste als eine durchgehende Linie. */
.sidebar-group::before,
.content::before {
  content: "";
  position: absolute;
  left: var(--balken-links, 0);
  top: 0;
  bottom: var(--balken-unten, 0);
  width: var(--strich, 3px);
  background: var(--balken, transparent);
}

/* Bei einem Bereich endet der senkrechte Strich und biegt nach rechts ab - ein
   Winkel wie in einem Baum. Gleiche Staerke wie der Strich selbst (4 px), genau
   so lang wie der Einzug, und auf derselben Hoehe wie das Strichende: darunter
   bleibt die Spalte frei, bevor der eingerueckte Strich der Personenzeilen
   weitergeht. */
.sidebar-group.subproject::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  /* Bis UNTER den eingerueckten Strich, nicht nur bis an ihn heran: sonst
     beruehren sich waagerecht und senkrecht nur in einem Punkt und die Ecke
     sieht wie eine Treppe aus. */
  width: calc(var(--einzug, 14px) + var(--strich, 3px));
  height: var(--strich, 3px);
  background: var(--balken, transparent);
}

.sidebar-group.spacer {
  background-color: rgb(250, 250, 250);
}

.content {
  padding: 8px;
  background: #fff;
}

.content.printview {
  display: flex;
  align-items: self-end;
  padding-left: 10px;
  height: 100%;
  font-size: 12px;
  border-bottom: 1px solid rgb(229 231 235 / var(--tw-border-opacity, 1)) !important;
}

.toggle {
  font-size: 0.8em;
}


/* Unterprojektnamen stehen in einer eigenen Box (Entwurf Bjoern, 04.09.2026):
   der Rahmen macht die Ebene sichtbar, ohne dass die Zeile wie eine zweite
   Projektzeile wirkt. */
.cat-icon {
  flex: none;
  color: #9aa5b1;
}

/* Ein Unterprojekt traegt seinen Namen ebenfalls in einer Box, aber im Rahmen
   der Projektfarbe - so ist auf einen Blick zu sehen, wohin es gehoert. */
.sidebar-group.subproject {
  padding-left: 14px;
}

.sidebar-group.subproject .sub-name {
  display: inline-block;
  max-width: 100%;
  padding: 1px 6px;
  border: 1px solid;
  border-radius: 3px;
  background: #fff;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Kleines Menue am Projektkopf. Schliesst ueber den globalen pointerdown-
   Handler in D3Planner, der ohnehin jede Auswahl aufhebt. */
.project-menu {
  position: fixed;
  z-index: 2500;
  min-width: 12rem;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.14);
  padding: 4px;
  font-weight: 400;
}

.project-menu-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 6px 10px;
  border: 0;
  background: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}
.project-menu-item:hover { background: #f1f5f9; }
</style>
